import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetSignedUploadUrlMutation, usePingEngineMutation } from '@common-reducers/backend-api/backend-api';
import { selectConnectedEnginesSupport } from '@common-reducers/ConnectedEnginesSlice';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectUserId } from '@common-reducers/UserSelectors';
import { isWhatsappExtensionContext } from '@common-services/detect-context';
import { DiagnosticsResultsTree } from '@common/components/diagnostics-results-tree';
import {
  diagnosticTests,
  DiagnosticTestDefinition,
  DiagnosticTestResult,
  TestSafety,
} from '@wa-communication-layer/diagnostics/test-registry';
import { DIAGNOSTIC_TEST_IMAGE_DATA_URL } from '@wa-communication-layer/diagnostics/test-media-constants';
import { getToolExecutor } from '@extension/context/content/thunks/DiagnosticsToolRunner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronRight, Minus, Info, ChevronLeft, ChevronsLeft, Paperclip, X } from 'lucide-react';
import { lsGet, lsSet } from '@common/views/diagnostics-view/diagnostics-helpers';
import { uploadFilePut } from '@common-services/utils';
import ReusableEmojiPickerButton from '@common/components/reusable-emoji-picker/reusable-emoji-picker-button';

// ── Constants ───────────────────────────────────────────────────────────────

const CHAT_ID_TESTS = new Set([
  'validation.validateChatId',
  'chat.getChatObjectByChatId',
  'chat.getChatMessages',
  'message.sendMessage',
  'message.sendTextMessage',
  'message.sendImageMessage',
  'message.sendPollMessage',
  'message.sendLinkPreviewMessage',
  'schedule.scheduleMessage',
  'schedule.scheduleTextMessage',
  'schedule.scheduleImageMessage',
  'schedule.schedulePollMessage',
  'schedule.scheduleLinkPreviewMessage',
  'chat.getProfilePicUrl',
  'message.messageStoreLookup',
]);

// Tests that are used by Team Inbox feature
const TEAM_INBOX_TESTS = new Set([
  'chat.queryContactsAndChats',    // Unified tool for contacts and chats (replaces old chat.getChats, chat.searchContactsAndChats)
  'chat.getChatObjectByChatId',    // requestChatByIdThunk
  'chat.sendSeen',                // requestSendSeenByChatIdThunk
  'chat.getProfilePicUrl',     // requestMissingChatsImagesThunk
  'session.getWaMeInfo',          // requestWaMeInfoThunk
  'media.getMessageMedia',        // requestMessageMediaThunk
  'message.sendMessage',
  'message.sendTextMessage',
  'message.sendImageMessage',
  'message.sendPollMessage',
  'message.sendLinkPreviewMessage',
]);

const SEND_MESSAGE_SECTION_TEST_IDS = [
  'message.sendMessage',
] as const;

const SEND_MESSAGE_SECTION_TEST_SET = new Set<string>(SEND_MESSAGE_SECTION_TEST_IDS);

const SCHEDULE_MESSAGE_SECTION_TEST_IDS = [
  'schedule.scheduleMessage',
] as const;

const SCHEDULE_MESSAGE_SECTION_TEST_SET = new Set<string>(SCHEDULE_MESSAGE_SECTION_TEST_IDS);
const STATUS_SECTION_TEST_IDS = [
  'status.createStatus',
] as const;
const STATUS_SECTION_TEST_SET = new Set<string>(STATUS_SECTION_TEST_IDS);
const SCHEDULE_MESSAGE_PRIMARY_TEST_ID = 'schedule.scheduleMessage';
const GLOBAL_CHAT_IDS_MIGRATION_VERSION = 5;

// Resolver keys that pull their value from the global chat-id fields
// (contact/group/newsletter). These are the ONLY defaults gated behind the
// per-test "Global" opt-in: everything else (static defaultValues, the image
// data-url resolver, futureDate, …) resolves independently and is always
// safe to apply. Kept in sync with resolveServerModeDefault below.
const CHAT_ID_LIKE_RESOLVERS = new Set<string>([
  'ownPhone',
  'ownChatId',
  'firstChatId',
  'firstGroupChatId',
]);

// Tests that should start with the "Global" chat-id toggle enabled so a
// first-time user gets a green run: any enabled test with a required input
// whose default is one of the global-dependent (chat-id-like) resolvers.
// Without this, those required inputs skip their resolver and fail with
// "X is required" even though global chat IDs are filled.
function computeDefaultGlobalChatIdTests(): Set<string> {
  const ids = new Set<string>();
  for (const test of diagnosticTests) {
    if (!test.enabled) continue;
    const hasChatIdLikeRequiredInput = test.inputs.some(
      (input) => input.required && !!input.defaultResolver && CHAT_ID_LIKE_RESOLVERS.has(input.defaultResolver),
    );
    if (hasChatIdLikeRequiredInput) ids.add(test.id);
  }
  return ids;
}

type ChatType = 'contact' | 'group' | 'newsletter';

// Human-readable placeholders for runtime-resolved inputs
const RESOLVER_PLACEHOLDERS: Record<string, string> = {
  ownPhone: 'e.g. 972548275828',
  ownChatId: 'e.g. 972548275828@c.us',
  firstGroupChatId: 'e.g. 120363XXX@g.us',
  latestMessageKey: 'e.g. true_972XXX@c.us_ABC123',
  optionalMessageKey: 'e.g. true_972XXX@c.us_ABC123',
  latestMediaMessageKey: 'e.g. true_972XXX@c.us_MEDIA123',
  testImageDataUrl: 'data:image/png;base64,...',
  latestOwnMessage: '(latest outgoing message)',
  futureDate: 'e.g. 2024-01-15T10:30:00Z',
};

const isWideInputField = (inputName: string): boolean =>
  inputName === 'url' ||
  inputName === 'content' ||
  inputName === 'settingsDescription' ||
  inputName === 'settingsJson';

const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

// ── Global Execution Plan ────────────────────────────────────────────────────
// Logic for expanding global chat IDs into execution batches.

interface ExecutionBatch {
  testIds: string[];
  chatIdOverride?: string;
  chatType?: ChatType;
  allGlobalIds?: string[]; // for profile pic special case
}

function buildGlobalExecutionPlan(
  testIds: string[],
  useGlobalChatIds: Set<string>,
  filledGlobals: { chatType: ChatType; chatId: string }[],
): ExecutionBatch[] {
  const batches: ExecutionBatch[] = [];

  let globalTests = testIds.filter(id => CHAT_ID_TESTS.has(id) && useGlobalChatIds.has(id));
  const nonGlobalTests = testIds.filter(id => !CHAT_ID_TESTS.has(id) || !useGlobalChatIds.has(id));

  // Profile pic test is special — runs ONCE with ALL global IDs, not once per chat type
  const profilePicTestId = 'chat.getProfilePicUrl';
  const hasGlobalProfilePicTest = globalTests.includes(profilePicTestId);
  if (hasGlobalProfilePicTest) {
    globalTests = globalTests.filter(id => id !== profilePicTestId);
  }

  // Batch 1: non-global tests (no chatId override)
  if (nonGlobalTests.length > 0) {
    batches.push({ testIds: nonGlobalTests });
  }

  // No global IDs filled — still run all global-enabled tests once (no override)
  // so the tool itself reports the missing-input error instead of silently dropping them.
  if (filledGlobals.length === 0) {
    const remaining = [...globalTests];
    if (hasGlobalProfilePicTest) remaining.push(profilePicTestId);
    if (remaining.length > 0) {
      batches.push({ testIds: remaining });
    }
    return batches;
  }

  // Profile pic with all global IDs at once
  if (hasGlobalProfilePicTest) {
    batches.push({
      testIds: [profilePicTestId],
      allGlobalIds: filledGlobals.map(g => g.chatId),
    });
  }

  // One batch per filled chat type for global-enabled tests
  for (const { chatType, chatId } of filledGlobals) {
    if (globalTests.length === 0) break;
    batches.push({ testIds: [...globalTests], chatIdOverride: chatId, chatType });
  }

  return batches;
}

// ── Server Mode Default Resolution ───────────────────────────────────────────
// In server mode (outside WhatsApp), we can't access WhatsApp Store to resolve
// smart defaults like ownPhone. Use global chat IDs as fallback.

interface ServerModeDefaultContext {
  globalContactChatId: string;
  globalGroupChatId: string;
  globalNewsletterChatId: string;
}

function resolveServerModeDefault(
  resolverKey: string,
  context: ServerModeDefaultContext
): any {
  switch (resolverKey) {
    case 'ownPhone':
      // Extract phone number from globalContactChatId (e.g., "972548275828@c.us" -> "972548275828")
      if (context.globalContactChatId) {
        return context.globalContactChatId.split('@')[0];
      }
      return undefined;
    case 'ownChatId':
    case 'firstChatId':
      return context.globalContactChatId || undefined;
    case 'firstGroupChatId':
      return context.globalGroupChatId || undefined;
    case 'testImageDataUrl':
      // 1x1 red pixel PNG as data URL for image tests
      return DIAGNOSTIC_TEST_IMAGE_DATA_URL;
    case 'futureDate':
      return new Date(Date.now() + 60 * 60 * 1000); // 1 hour from now
    default:
      return undefined;
  }
}

// ── Props ───────────────────────────────────────────────────────────────────

interface DiagnosticsPanelProps {
  /** Optional - only for admin/support users diagnosing another user's engine.
   * When omitted, uses the authenticated user's ID for engine filtering,
   * and the backend uses the authenticated user from JWT for tool execution.
   */
  supportedUserId?: string;
  vertical?: boolean;
}

// ── Component ───────────────────────────────────────────────────────────────

export function DiagnosticsPanel({ supportedUserId, vertical }: DiagnosticsPanelProps) {
  const connectedEngines = useSelector(selectConnectedEnginesSupport);
  const authenticatedUserId = useAppSelector(selectUserId);
  const appDispatch = useAppDispatch();

  // Use supportedUserId if provided (admin mode), otherwise use authenticated user
  const effectiveUserId = supportedUserId || authenticatedUserId || '';
  const [getSignedUploadUrl] = useGetSignedUploadUrlMutation();
  const [pingEngine] = usePingEngineMutation();
  const [isLoading, setIsLoading] = useState(false);

  // Engine — auto-selected below via effect
  const [selectedEngineId, setSelectedEngineId] = useState('');

  // Engine health status: 'alive' | 'dead' | 'pinging'
  const [engineHealth, setEngineHealth] = useState<Record<string, 'alive' | 'dead' | 'pinging'>>({});

  // Test selection (persisted)
  // Filter out any stale test IDs that no longer exist in the registry
  const [selectedTests, setSelectedTests] = useState<Set<string>>(() => {
    const saved = lsGet<string[] | null>('selectedTests', null);
    if (saved) {
      // Filter out stale test IDs from localStorage
      const validIds = diagnosticTests.map(t => t.id);
      const validSet = new Set(validIds);
      return new Set(saved.filter(id => validSet.has(id)));
    }
    const initial = new Set<string>();
    diagnosticTests.filter(t => t.enabled && t.safety === 'safe').forEach(t => initial.add(t.id));
    return initial;
  });
  const [inputOverrides, setInputOverrides] = useState<Record<string, Record<string, any>>>({});
  const [trimResponse, setTrimResponse] = useState(() => lsGet('trimResponse', true));

  // Global chat IDs (persisted)
  const [globalContactChatId, setGlobalContactChatId] = useState(() => lsGet('globalContactChatId', ''));
  const [globalGroupChatId, setGlobalGroupChatId] = useState(() => lsGet('globalGroupChatId', ''));
  const [globalNewsletterChatId, setGlobalNewsletterChatId] = useState(() => lsGet('globalNewsletterChatId', ''));
  const [useGlobalChatIds, setUseGlobalChatIds] = useState<Set<string>>(() => {
    const saved = lsGet<string[] | null>('useGlobalChatIds', null);
    if (saved) return new Set(saved);
    // Fresh user: seed from the registry so every test with a required
    // chat-id-like input starts global-enabled (green first run).
    return computeDefaultGlobalChatIdTests();
  });
  const prevSelectedTestsRef = useRef<Set<string>>(new Set(selectedTests));

  // Results & UI (collapsed domains persisted)
  const [results, setResults] = useState<any>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [collapsedDomains, setCollapsedDomains] = useState<Set<string>>(() => {
    const saved = lsGet<string[] | null>('collapsedDomains', null);
    if (saved) return new Set(saved);
    return new Set();
  });

  // ── Persist to localStorage ───────────────────────────────────────────

  useEffect(() => { lsSet('selectedEngineId', selectedEngineId); }, [selectedEngineId]);
  useEffect(() => { lsSet('selectedTests', [...selectedTests]); }, [selectedTests]);
  useEffect(() => { lsSet('trimResponse', trimResponse); }, [trimResponse]);
  useEffect(() => { lsSet('globalContactChatId', globalContactChatId); }, [globalContactChatId]);
  useEffect(() => { lsSet('globalGroupChatId', globalGroupChatId); }, [globalGroupChatId]);
  useEffect(() => { lsSet('globalNewsletterChatId', globalNewsletterChatId); }, [globalNewsletterChatId]);
  useEffect(() => { lsSet('useGlobalChatIds', [...useGlobalChatIds]); }, [useGlobalChatIds]);
  useEffect(() => { lsSet('collapsedDomains', [...collapsedDomains]); }, [collapsedDomains]);

  useEffect(() => {
    const wasSelected = prevSelectedTestsRef.current.has(SCHEDULE_MESSAGE_PRIMARY_TEST_ID);
    const isSelected = selectedTests.has(SCHEDULE_MESSAGE_PRIMARY_TEST_ID);
    if (!wasSelected && isSelected) {
      const currentDueDate = inputOverrides[SCHEDULE_MESSAGE_PRIMARY_TEST_ID]?.dueDate;
      if (typeof currentDueDate !== 'string' || currentDueDate.trim().length === 0) {
        setInputOverrides(prev => ({
          ...prev,
          [SCHEDULE_MESSAGE_PRIMARY_TEST_ID]: {
            ...prev[SCHEDULE_MESSAGE_PRIMARY_TEST_ID],
            dueDate: new Date(Date.now() + (60 * 1000)).toISOString(),
          },
        }));
      }
    }
    prevSelectedTestsRef.current = new Set(selectedTests);
  }, [inputOverrides, selectedTests]);

  // One-time migration: ensure send/schedule tests start with Global enabled by default.
  useEffect(() => {
    const migrationVersion = lsGet<number>('useGlobalChatIdsMigrationVersion', 0);
    if (migrationVersion >= GLOBAL_CHAT_IDS_MIGRATION_VERSION) return;

    setUseGlobalChatIds((prev) => {
      const next = new Set(prev);
      // Seed the registry-derived default set once so existing users pick up
      // the new chat-id-like tests (green first run) without losing their
      // per-test opt-outs.
      for (const defaultTestId of computeDefaultGlobalChatIdTests()) {
        next.add(defaultTestId);
      }
      for (const sendTestId of SEND_MESSAGE_SECTION_TEST_IDS) {
        next.add(sendTestId);
      }
      for (const scheduleTestId of SCHEDULE_MESSAGE_SECTION_TEST_IDS) {
        next.add(scheduleTestId);
      }
      return next;
    });

    lsSet('useGlobalChatIdsMigrationVersion', GLOBAL_CHAT_IDS_MIGRATION_VERSION);
  }, []);

  // ── Derived ─────────────────────────────────────────────────────────────

  const allTests = useMemo(() => diagnosticTests.filter(t => t.enabled), []);
  const allTestIds = useMemo(() => new Set(allTests.map((t) => t.id)), [allTests]);
  const validTeamInboxTestIds = useMemo(
    () => [...TEAM_INBOX_TESTS].filter((id) => allTestIds.has(id)),
    [allTestIds]
  );

  const testsByDomain = useMemo(() => {
    const map = new Map<string, DiagnosticTestDefinition[]>();
    for (const t of allTests) {
      const list = map.get(t.domain) ?? [];
      list.push(t);
      map.set(t.domain, list);
    }
    return map;
  }, [allTests]);

  const userEngines = connectedEngines.filter(e => e.id?.includes(effectiveUserId) && e.id?.toLowerCase().startsWith('whatsapp'));
  const allSelected = allTests.length > 0 && allTests.every(t => selectedTests.has(t.id));
  const selectedCount = allTests.filter(t => selectedTests.has(t.id)).length;

  // Remove stale test IDs loaded from localStorage (e.g. removed/renamed tests)
  useEffect(() => {
    setSelectedTests((prev) => {
      let hasInvalid = false;
      const filtered = new Set<string>();
      for (const id of prev) {
        if (allTestIds.has(id)) {
          filtered.add(id);
        } else {
          hasInvalid = true;
        }
      }
      return hasInvalid ? filtered : prev;
    });
  }, [allTestIds]);

  // Remove stale "global chat id" toggles for tests that no longer exist
  useEffect(() => {
    setUseGlobalChatIds((prev) => {
      let hasInvalid = false;
      const filtered = new Set<string>();
      for (const id of prev) {
        if (allTestIds.has(id)) {
          filtered.add(id);
        } else {
          hasInvalid = true;
        }
      }
      return hasInvalid ? filtered : prev;
    });
  }, [allTestIds]);

  // ── Auto-select engine ─────────────────────────────────────────────────
  // When engines change: clear stale selection, auto-pick first available

  useEffect(() => {
    // If the selected engine disconnected, clear it
    if (selectedEngineId && !userEngines.some(e => e.id === selectedEngineId)) {
      setSelectedEngineId('');
      return;
    }

    // If nothing selected and engines are available, pick the first one
    if (!selectedEngineId && userEngines.length > 0) {
      const waEngine = userEngines.find(
        e => e.id?.toLowerCase().startsWith('whatsapp')
      );
      setSelectedEngineId(waEngine?.id ?? userEngines[0].id);
    }
  }, [userEngines, selectedEngineId]);

  // ── Ping engines to detect dead sockets ─────────────────────────────────

  const pingAllEngines = useCallback(async () => {
    for (const engine of userEngines) {
      const id = engine.id;
      if (!id) continue;
      setEngineHealth(prev => ({ ...prev, [id]: 'pinging' }));
      try {
        const result = await pingEngine({ engineId: id }).unwrap();
        setEngineHealth(prev => ({ ...prev, [id]: result?.success ? 'alive' : 'dead' }));
      } catch {
        setEngineHealth(prev => ({ ...prev, [id]: 'dead' }));
      }
    }
  }, [userEngines, pingEngine]);

  // Ping all engines when the engine list changes
  const prevEngineIdsRef = useRef<string>('');
  useEffect(() => {
    const engineIds = userEngines.map(e => e.id).sort().join(',');
    if (engineIds && engineIds !== prevEngineIdsRef.current) {
      prevEngineIdsRef.current = engineIds;
      pingAllEngines();
    }
  }, [userEngines, pingAllEngines]);

  // ── Safety helpers ──────────────────────────────────────────────────────

  const isSafetyFullySelected = (level: TestSafety) =>
    allTests.filter(t => t.safety === level).every(t => selectedTests.has(t.id));
  const isSafetyPartiallySelected = (level: TestSafety) => {
    const levelTests = allTests.filter(t => t.safety === level);
    const count = levelTests.filter(t => selectedTests.has(t.id)).length;
    return count > 0 && count < levelTests.length;
  };

  // ── Handlers ────────────────────────────────────────────────────────────

  // Bulk-arming the dangerous tests (page reload, QR timeout, WhatsApp
  // logout) in one click is how a live session gets disconnected by
  // accident — require an explicit confirmation before selecting them as
  // a group. Individually checking a dangerous test stays one click.
  const confirmDangerousBulkSelect = (count: number) =>
    window.confirm(
      `Select ${count} DANGEROUS test${count === 1 ? '' : 's'} (page reload, QR timeout, WhatsApp logout)? ` +
      'Running these can disconnect the live WhatsApp session.',
    );

  const handleSafetyToggle = (level: TestSafety) => {
    const levelTests = allTests.filter(t => t.safety === level);
    const allSel = levelTests.every(t => selectedTests.has(t.id));
    if (level === 'dangerous' && !allSel && !confirmDangerousBulkSelect(levelTests.length)) {
      return;
    }
    setSelectedTests(prev => {
      const next = new Set(prev);
      for (const t of levelTests) {
        if (allSel) next.delete(t.id);
        else next.add(t.id);
      }
      return next;
    });
  };

  const handleTestToggle = (testId: string) => {
    setSelectedTests(prev => {
      const next = new Set(prev);
      if (next.has(testId)) next.delete(testId);
      else next.add(testId);
      return next;
    });
  };

  const handleSelectAll = () => {
    // Same guard as the dangerous level toggle: "Select All" silently arming
    // logout/QR-timeout is the same accident waiting to happen. If the user
    // declines, still select everything else.
    let includeDangerous = true;
    if (!allSelected) {
      const newlyArmed = allTests.filter(t => t.safety === 'dangerous' && !selectedTests.has(t.id));
      if (newlyArmed.length > 0) {
        includeDangerous = confirmDangerousBulkSelect(newlyArmed.length);
      }
    }
    setSelectedTests(prev => {
      const next = new Set(prev);
      for (const t of allTests) {
        if (allSelected) next.delete(t.id);
        else if (includeDangerous || t.safety !== 'dangerous') next.add(t.id);
      }
      return next;
    });
  };

  const handleInputChange = (testId: string, inputName: string, value: string | boolean | number) => {
    setInputOverrides(prev => ({
      ...prev,
      [testId]: { ...prev[testId], [inputName]: value },
    }));
  };

  const handleDiagnosticFileSelected = async (testId: string, file: File): Promise<void> => {
    const isStatusCreate = testId === 'status.createStatus';
    const isGroupSetPicture = testId === 'group.setPicture';
    const useLocalDataUrl =
      isGroupSetPicture || (isStatusCreate && isWhatsappExtensionContext());

    try {
      if (useLocalDataUrl) {
        const dataUrl = await fileToDataUrl(file);
        handleInputChange(testId, 'fileDataUrl', dataUrl);
        handleInputChange(testId, 'fileName', file.name);
        handleInputChange(testId, 'fileMimeType', file.type);
        return;
      }

      const uploadUrlResponse: { url: string; publicUrl: string } = await getSignedUploadUrl(file.type).unwrap();
      await uploadFilePut(file, uploadUrlResponse.url);
      handleInputChange(testId, 'fileDataUrl', uploadUrlResponse.publicUrl);
      handleInputChange(testId, 'fileName', file.name);
      handleInputChange(testId, 'fileMimeType', file.type);
    } catch {
      // For status tool, never fallback to huge data URLs (they can timeout over MQTT/server path).
      if (isStatusCreate && !useLocalDataUrl) {
        setToast({ message: 'Failed to upload media URL for createStatusTool. Please retry.', type: 'error' });
        return;
      }

      try {
        const dataUrl = await fileToDataUrl(file);
        handleInputChange(testId, 'fileDataUrl', dataUrl);
        handleInputChange(testId, 'fileName', file.name);
        handleInputChange(testId, 'fileMimeType', file.type);
      } catch {
        setToast({ message: 'Failed to process selected file', type: 'error' });
      }
    }
  };

  const handleGlobalToggle = (testId: string) => {
    setUseGlobalChatIds(prev => {
      const next = new Set(prev);
      if (next.has(testId)) next.delete(testId);
      else next.add(testId);
      return next;
    });
  };

  const toggleDomain = (domain: string) => {
    setCollapsedDomains(prev => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });
  };

  // Compute filled global chat IDs from state
  const getFilledGlobals = useCallback(() => {
    const filled: { chatType: ChatType; chatId: string }[] = [];
    if (globalContactChatId.trim()) filled.push({ chatType: 'contact', chatId: globalContactChatId.trim() });
    if (globalGroupChatId.trim()) filled.push({ chatType: 'group', chatId: globalGroupChatId.trim() });
    if (globalNewsletterChatId.trim()) filled.push({ chatType: 'newsletter', chatId: globalNewsletterChatId.trim() });
    return filled;
  }, [globalContactChatId, globalGroupChatId, globalNewsletterChatId]);

  const normalizeDiagnosticError = (err: any): { message: string; stack?: string } => {
    if (!err) return { message: 'Unknown error' };
    if (typeof err === 'string') return { message: err };
    if (err instanceof Error) return { message: err.message || 'Unknown error', stack: err.stack };

    const candidates = [
      err?.message,
      err?.errorMessage,
      typeof err?.error === 'string' ? err.error : err?.error?.message,
      err?.data?.message,
      typeof err?.data?.error === 'string' ? err.data.error : err?.data?.error?.message,
    ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

    if (candidates.length > 0) {
      return { message: candidates[0], stack: err?.stack || err?.data?.stack };
    }

    try {
      return { message: JSON.stringify(err), stack: err?.stack || err?.data?.stack };
    } catch {
      return { message: String(err), stack: err?.stack || err?.data?.stack };
    }
  };

  // ── Direct Mode: run a single test with given inputs ─────────────────────

  const runDirectSingleTest = async (
    testId: string,
    chatIdOverride?: string,
    chatType?: ChatType,
    allGlobalIds?: string[],
  ): Promise<DiagnosticTestResult> => {
    const testDef = diagnosticTests.find(t => t.id === testId);
    if (!testDef) {
      return {
        id: testId, name: testId, domain: 'unknown', status: 'error',
        duration: 0, error: { message: `Test definition not found: ${testId}` }, safety: 'safe',
      };
    }

    // Extension-only tools (validateStores, updateShouldAppearChatsByList)
    // mutate local WhatsApp UI/store state and return EXTENSION_ONLY over
    // MQTT by design. In server mode (this page, outside WhatsApp Web) mark
    // them skipped-by-design instead of counting a failure.
    if (testDef.extensionOnly && !isWhatsappExtensionContext()) {
      return {
        id: testId, name: testDef.name, domain: testDef.domain, status: 'skipped',
        duration: 0, safety: testDef.safety, toolName: testDef.toolName,
        response: { skipped: true, reason: 'extension-only by design (runs only inside WhatsApp Web)' },
      };
    }

    const executor = getToolExecutor(testId);
    if (!executor) {
      return {
        id: testId, name: testDef.name, domain: testDef.domain, status: 'error',
        duration: 0, error: { message: `No tool executor found for: ${testId}` }, safety: testDef.safety,
        toolName: testDef.toolName,
      };
    }

    // Build inputs from overrides, defaultResolver, and defaults
    const inputs: Record<string, any> = {};
    for (const input of testDef.inputs) {
      const override = inputOverrides[testId]?.[input.name];
      const hasOverride = override !== undefined;

      if (hasOverride) {
        if (input.type === 'number' && typeof override === 'string') {
          const parsed = Number(override);
          inputs[input.name] = isNaN(parsed) ? input.defaultValue : parsed;
        } else {
          inputs[input.name] = override;
        }
      } else if (input.defaultResolver) {
        // Only chat-id-like resolvers (which read the global chat-id fields)
        // are gated behind the per-test Global opt-in. Static resolvers like
        // testImageDataUrl / futureDate resolve independently and must always
        // apply, otherwise required inputs fail with "X is required".
        const isChatIdLikeResolver = CHAT_ID_LIKE_RESOLVERS.has(input.defaultResolver);
        const skipResolver = input.required && isChatIdLikeResolver && !useGlobalChatIds.has(testId);
        if (!skipResolver) {
          const resolvedValue = resolveServerModeDefault(input.defaultResolver, {
            globalContactChatId, globalGroupChatId, globalNewsletterChatId,
          });
          if (resolvedValue !== undefined) {
            inputs[input.name] = resolvedValue;
          }
        }
      } else if (input.defaultValue !== undefined) {
        // Static defaults never depend on global chat IDs, so always apply
        // them (a first run should use the built-in default, not fail as
        // "X is required").
        inputs[input.name] = input.defaultValue;
      }
    }

    // Preserve extra per-test overrides (e.g. fileName/fileMimeType from file uploader)
    const testOverrides = inputOverrides[testId] || {};
    Object.entries(testOverrides).forEach(([key, value]) => {
      if (inputs[key] !== undefined) return;
      if (value === undefined) return;
      if (typeof value === 'string' && value === '') return;
      inputs[key] = value;
    });

    if (supportedUserId) {
      inputs.supportedUserId = supportedUserId;
    }

    // Apply chatId override from global execution plan
    if (chatIdOverride) {
      inputs.chatId = chatIdOverride;
    }

    // Profile pic special case — all global IDs at once
    if (allGlobalIds && allGlobalIds.length > 0) {
      inputs.contactIds = allGlobalIds;
    }

    const testStart = Date.now();
    try {
      console.log(`[Diagnostics Direct] Running ${testId}`, inputs);
      const response = await executor(appDispatch, inputs);
      const duration = Date.now() - testStart;
      // If the response itself contains an error field, treat as error.
      // Also inspect the nested `data.error` (a truthy value): tools that wrap
      // a page failure as `{ success:true, data:{ result:false, error } }`
      // (e.g. addOrEditCustomerNote / changeLabels on a non-business account)
      // must count as FAIL, not PASS. A bare `data.result === false` with NO
      // error is a legitimate no-op (mute-already-muted, unarchive) and stays
      // a PASS.
      const responseData = response && typeof response === 'object' ? (response as any).data : undefined;
      const nestedErrorPresent = !!(responseData && typeof responseData === 'object' && (responseData as any).error);
      const hasErrorInResponse = response && typeof response === 'object' && ('error' in response || nestedErrorPresent || ('isValid' in response && !response.isValid));
      const status = hasErrorInResponse ? 'error' : 'pass';
      console.log(`[Diagnostics Direct] ${status.toUpperCase()}: ${testId} (${duration}ms)`, response);
      return {
        id: testId, name: testDef.name, domain: testDef.domain, status,
        duration, response: trimResponse ? sanitizeDirectResponse(response) : response,
        safety: testDef.safety, inputs, chatType,
        toolName: testDef.toolName,
      };
    } catch (err: any) {
      const duration = Date.now() - testStart;
      const normalizedError = normalizeDiagnosticError(err);
      console.error(`[Diagnostics Direct] ERROR: ${testId} (${duration}ms):`, err);
      return {
        id: testId, name: testDef.name, domain: testDef.domain, status: 'error',
        duration, error: { message: normalizedError.message, stack: normalizedError.stack },
        safety: testDef.safety, inputs, chatType,
        toolName: testDef.toolName,
      };
    }
  };

  // ── Direct Mode Execution (streaming) ─────────────────────────────────────

  const runDirectMode = async (testIds: string[]) => {
    const startTime = Date.now();

    const batches = buildGlobalExecutionPlan(testIds, useGlobalChatIds, getFilledGlobals());

    // Build placeholder "running" entries for every test across all batches
    const placeholders: DiagnosticTestResult[] = [];
    for (const batch of batches) {
      for (const testId of batch.testIds) {
        const testDef = diagnosticTests.find(t => t.id === testId);
        placeholders.push({
          id: testId,
          name: testDef?.name ?? testId,
          domain: testDef?.domain ?? 'unknown',
          status: 'running',
          duration: 0,
          safety: testDef?.safety ?? 'safe',
          chatType: batch.chatType,
          toolName: testDef?.toolName,
        });
      }
    }

    // Show all tests as "running" immediately
    const buildSummary = (results: DiagnosticTestResult[]) => {
      const passed = results.filter(r => r.status === 'pass').length;
      const failed = results.filter(r => r.status === 'fail').length;
      const errored = results.filter(r => r.status === 'error').length;
      const skipped = results.filter(r => r.status === 'skipped').length;
      const running = results.filter(r => r.status === 'running').length;
      return { total: results.length, passed, failed, errored, skipped, running };
    };

    setResults({
      results: placeholders,
      summary: buildSummary(placeholders),
      durationMs: 0,
    });

    // Track completed results keyed by "testId-chatType" to replace placeholders
    const completedMap = new Map<string, DiagnosticTestResult>();
    const resultKey = (testId: string, chatType?: string) => `${testId}-${chatType ?? 'default'}`;

    for (const batch of batches) {
      for (const testId of batch.testIds) {
        const result = await runDirectSingleTest(testId, batch.chatIdOverride, batch.chatType, batch.allGlobalIds);
        completedMap.set(resultKey(testId, batch.chatType), result);

        // Merge: replace placeholders with completed results, keep remaining as "running"
        const merged = placeholders.map(p => {
          const key = resultKey(p.id, p.chatType);
          return completedMap.get(key) ?? p;
        });
        const totalDuration = Date.now() - startTime;

        setResults({
          results: merged,
          summary: buildSummary(merged),
          durationMs: totalDuration,
        });
      }
    }

    const totalDuration = Date.now() - startTime;
    const finalResults = placeholders.map(p => {
      const key = resultKey(p.id, p.chatType);
      return completedMap.get(key) ?? p;
    });

    return {
      results: finalResults,
      summary: buildSummary(finalResults),
      durationMs: totalDuration,
    };
  };

  // Helper to sanitize direct mode responses
  const sanitizeDirectResponse = (response: any): any => {
    if (response === null || response === undefined) return response;
    try {
      const json = JSON.stringify(response);
      if (json.length > 2000) {
        return { _truncated: true, _originalSize: json.length, preview: json.slice(0, 2000) };
      }
      return JSON.parse(json);
    } catch {
      return { _type: typeof response, _toString: String(response).slice(0, 300) };
    }
  };

  const renderUnifiedStyleToolRow = (test: DiagnosticTestDefinition, options?: { enableDueDateShortcuts?: boolean; stackTextFields?: boolean }) => {
    const stackTextFields = options?.stackTextFields === true;
    const hasChatId = CHAT_ID_TESTS.has(test.id);
    const isGlobal = hasChatId && useGlobalChatIds.has(test.id);
    const isSelected = selectedTests.has(test.id);
    const isTeamInbox = TEAM_INBOX_TESTS.has(test.id);

    return (
      <div key={test.id} className="flex items-center gap-1.5 py-0.5 flex-wrap">
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => handleTestToggle(test.id)}
          className="size-3.5"
        />
        <span className="text-xs min-w-[180px]">
          {test.name}
          {test.safety !== 'safe' && (
            <span className={cn('ml-1 text-[10px]', safetyColor(test.safety))}>
              ({test.safety})
            </span>
          )}
          {isTeamInbox && (
            <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 h-4 border-cyan-400 text-cyan-600 bg-cyan-50">
              Team Inbox
            </Badge>
          )}
          {test.toolName ? (
            <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 h-4 border-green-400 text-green-600 bg-green-50">
              {test.toolName}
            </Badge>
          ) : (
            <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 h-4 border-slate-300 text-slate-400 bg-slate-50">
              missing tool
            </Badge>
          )}
        </span>

        {hasChatId && (
          <label className="flex items-center gap-1 cursor-pointer ml-1 shrink-0">
            <Checkbox
              checked={isGlobal}
              onCheckedChange={() => handleGlobalToggle(test.id)}
              className="size-3"
              disabled={!isSelected}
            />
            <span className="text-[10px] text-slate-500">Global</span>
          </label>
        )}

        {test.inputs
          .filter((input) => {
            if (!hasChatId || !isGlobal) return true;
            if (input.name === 'chatId') return false;
            return true;
          })
          .map((input) => {
            const currentValue = inputOverrides[test.id]?.[input.name];
            const effectiveValue = currentValue !== undefined ? currentValue : input.defaultValue;
            const isDisabled = !isSelected;

            if (input.name === 'fileMimeType') {
              return null;
            }

            if (input.name === 'fileDataUrl') {
              const fileInputId = `diagnostic-file-${test.id.replace(/\./g, '-')}`;
              const normalizedFileValue = typeof currentValue === 'string' ? currentValue.trim() : '';
              const hasSelectedFile = normalizedFileValue.length > 0;
              const isRemoteUploadUrl = /^https?:\/\//i.test(normalizedFileValue);
              const fileTooltip = !hasSelectedFile
                ? 'Choose file'
                : isRemoteUploadUrl
                  ? `Upload URL: ${normalizedFileValue}`
                  : 'Upload URL: local data URL';
              return (
                <div key={input.name} className="flex items-center gap-1 shrink-0">
                  <label
                    htmlFor={fileInputId}
                    title={fileTooltip}
                    aria-label={fileTooltip}
                    className={cn(
                      'inline-flex h-6 w-6 items-center justify-center rounded-md border',
                      hasSelectedFile ? 'border-emerald-400 bg-emerald-50' : 'bg-white',
                      isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-slate-50'
                    )}
                  >
                    <Paperclip className={cn('size-3.5', hasSelectedFile ? 'text-emerald-700' : 'text-slate-600')} />
                  </label>
                  <input
                    id={fileInputId}
                    type="file"
                    accept="image/*,video/*,*/*"
                    disabled={isDisabled}
                    className="hidden"
                    onChange={async (e) => {
                      const inputEl = e.currentTarget as HTMLInputElement | null;
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        await handleDiagnosticFileSelected(test.id, file);
                      } finally {
                        if (inputEl) {
                          inputEl.value = '';
                        }
                      }
                    }}
                  />
                  {hasSelectedFile && (
                    <button
                      type="button"
                      className={cn(
                        'inline-flex h-5 w-5 items-center justify-center rounded border border-red-300 bg-red-50 text-red-600',
                        isDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-red-100'
                      )}
                      disabled={isDisabled}
                      onClick={() => {
                        handleInputChange(test.id, 'fileDataUrl', '');
                        handleInputChange(test.id, 'fileName', '');
                        handleInputChange(test.id, 'fileMimeType', '');
                      }}
                      title="Clear file"
                      aria-label="Clear file"
                    >
                      <X className="size-3" />
                    </button>
                  )}
                </div>
              );
            }

            if (options?.enableDueDateShortcuts && input.name === 'dueDate') {
              const setQuickDueDate = (minutesToAdd: number) => {
                const dueDateIso = new Date(Date.now() + (minutesToAdd * 60 * 1000)).toISOString();
                handleInputChange(test.id, input.name, dueDateIso);
              };

              return (
                <div key={input.name} className={cn("flex items-center gap-1", stackTextFields ? "w-full" : "shrink-0")} title={input.label}>
                  <span className={cn("text-slate-500", stackTextFields ? "text-[11px] min-w-[120px]" : "text-[9px]")}>{input.label}:</span>
                  <Input
                    type="text"
                    value={currentValue ?? ''}
                    onChange={e => handleInputChange(test.id, input.name, e.target.value)}
                    placeholder={
                      input.defaultResolver
                        ? RESOLVER_PLACEHOLDERS[input.defaultResolver] ?? `(${input.defaultResolver})`
                        : input.defaultValue !== undefined
                          ? String(input.defaultValue)
                          : ''
                    }
                    disabled={isDisabled}
                    className={cn(stackTextFields ? "h-8 text-[12px] flex-1" : "h-6 text-[11px] w-[180px]")}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-5 px-1.5 text-[9px]"
                    disabled={isDisabled}
                    onClick={() => setQuickDueDate(1)}
                  >
                    +1
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-5 px-1.5 text-[9px]"
                    disabled={isDisabled}
                    onClick={() => setQuickDueDate(2)}
                  >
                    +2
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-5 px-1.5 text-[9px]"
                    disabled={isDisabled}
                    onClick={() => setQuickDueDate(5)}
                  >
                    +5
                  </Button>
                </div>
              );
            }

            if (input.type === 'boolean') {
              const isChecked = effectiveValue === true || effectiveValue === 'true';
              return (
                <label
                  key={input.name}
                  className={cn(
                    "flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] cursor-pointer shrink-0",
                    isChecked ? "bg-blue-50 border-blue-300" : "bg-slate-50 border-slate-200",
                    isDisabled && "opacity-50 cursor-not-allowed"
                  )}
                  title={input.label}
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={(checked) => handleInputChange(test.id, input.name, checked)}
                    disabled={isDisabled}
                    className="size-3"
                  />
                  <span className={cn(isChecked ? "text-blue-700" : "text-slate-600")}>
                    {input.label}
                  </span>
                </label>
              );
            }

            if (input.type === 'number') {
              return (
                <div key={input.name} className={cn("flex items-center gap-1", stackTextFields ? "w-full" : "shrink-0")} title={input.label}>
                  <span className={cn("text-slate-500", stackTextFields ? "text-[11px] min-w-[120px]" : "text-[9px]")}>{input.label}:</span>
                  <Input
                    type="number"
                    value={currentValue ?? ''}
                    onChange={e => handleInputChange(test.id, input.name, e.target.value)}
                    placeholder={input.defaultValue !== undefined ? String(input.defaultValue) : ''}
                    disabled={isDisabled}
                    className={cn(stackTextFields ? "h-8 text-[12px] flex-1" : "h-6 text-[11px] w-[60px]")}
                  />
                </div>
              );
            }

            const isReactionEmojiInput = test.id === 'message.reactToMessage' && input.name === 'text';

            return (
              <div key={input.name} className={cn("flex items-center gap-1", stackTextFields ? "w-full" : "shrink-0")} title={input.label}>
                <span className={cn("text-slate-500", stackTextFields ? "text-[11px] min-w-[120px]" : "text-[9px]")}>{input.label}:</span>
                <Input
                  type="text"
                  value={currentValue ?? ''}
                  onChange={e => handleInputChange(test.id, input.name, e.target.value)}
                  placeholder={
                    input.defaultResolver
                      ? RESOLVER_PLACEHOLDERS[input.defaultResolver] ?? `(${input.defaultResolver})`
                      : input.defaultValue !== undefined
                        ? String(input.defaultValue)
                        : ''
                  }
                  disabled={isDisabled}
                  className={cn(
                    stackTextFields
                      ? "h-8 text-[12px] flex-1"
                      : cn("h-6 text-[11px]", isWideInputField(input.name) ? "w-[280px]" : "w-[120px]")
                  )}
                />
                {isReactionEmojiInput && (
                  <ReusableEmojiPickerButton
                    disabled={isDisabled}
                    onEmojiSelect={emoji => handleInputChange(test.id, input.name, emoji)}
                  />
                )}
              </div>
            );
          })}
      </div>
    );
  };

  // ── Run handler ─────────────────────────────────────────────────────────

  const handleRun = async () => {
    const testIds = allTests.filter(t => selectedTests.has(t.id)).map(t => t.id);
    if (testIds.length === 0) {
      setToast({ message: 'Please select at least one test', type: 'error' });
      return;
    }

    setIsLoading(true);

    try {
      const result = await runDirectMode(testIds);
      setResults(result);
      setToast({
        message: `Diagnostics complete: ${result.summary.passed}/${result.summary.total} passed`,
        type: 'success',
      });
    } catch (error: any) {
      const msg = error?.message || 'Diagnostics failed';
      console.error('[Diagnostics] failed:', error);
      setToast({ message: msg, type: 'error' });
      setResults({
        results: [],
        summary: { total: 0, passed: 0, failed: 0, errored: 1, skipped: 0 },
        durationMs: 0,
        error: msg,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-hide toast
  if (toast) {
    setTimeout(() => setToast(null), 4000);
  }

  // ── Render helpers ──────────────────────────────────────────────────────

  const safetyColor = (level: TestSafety) => {
    if (level === 'dangerous') return 'text-red-600';
    if (level === 'write') return 'text-amber-600';
    return '';
  };

  // ── Form panel ──────────────────────────────────────────────────────────

  const formPanel = (
    <div className={cn('flex flex-col gap-4', !vertical && 'flex-1 min-w-0 overflow-y-auto')}>
      {/* Engine selector */}
      <div>
        <Label className="text-xs mb-1.5">Engine</Label>
        {userEngines.length === 0 ? (
          <p className="text-sm text-slate-500">
            No engines connected for this user. The user must have an active WhatsApp connection.
          </p>
        ) : (
          <>
            <Select value={selectedEngineId} onValueChange={setSelectedEngineId}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select engine..." />
              </SelectTrigger>
              <SelectContent>
                {userEngines.map(engine => {
                  const health = engineHealth[engine.id];
                  return (
                    <SelectItem key={engine.id} value={engine.id} className="text-xs">
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className={cn(
                            'inline-block size-2 rounded-full shrink-0',
                            health === 'alive' && 'bg-green-500',
                            health === 'dead' && 'bg-red-500',
                            health === 'pinging' && 'bg-yellow-400 animate-pulse',
                            !health && 'bg-gray-300',
                          )}
                          title={health === 'alive' ? 'Responding' : health === 'dead' ? 'Not responding' : health === 'pinging' ? 'Checking...' : 'Unknown'}
                        />
                        {engine.id} ({engine.browserType} - {engine.domain})
                        {health === 'dead' && <span className="text-red-500 text-[10px] ml-1">(dead)</span>}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="sm"
              onClick={pingAllEngines}
              className="h-6 px-2 text-[10px] mt-1"
            >
              Refresh engine status
            </Button>
          </>
        )}
      </div>

      {/* Safety levels + Trim */}
      <div>
        <Label className="text-xs mb-1.5">Safety Levels</Label>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {(['safe', 'write', 'dangerous'] as TestSafety[]).map(level => {
            const full = isSafetyFullySelected(level);
            const partial = isSafetyPartiallySelected(level);
            return (
              <label key={level} className="flex items-center gap-1.5 cursor-pointer">
                <span className="relative flex items-center">
                  <Checkbox
                    checked={full || partial}
                    onCheckedChange={() => handleSafetyToggle(level)}
                    className="size-3.5"
                  />
                  {partial && !full && (
                    <Minus className="absolute inset-0 m-auto size-2.5 text-primary pointer-events-none" />
                  )}
                </span>
                <span className={cn('text-xs', safetyColor(level))}>
                  {level === 'safe' && 'Safe (read-only)'}
                  {level === 'write' && 'Write (sends messages)'}
                  {level === 'dangerous' && 'Dangerous (reload, logout)'}
                </span>
              </label>
            );
          })}
          <label className="flex items-center gap-1.5 cursor-pointer ml-2">
            <Checkbox
              checked={trimResponse}
              onCheckedChange={() => setTrimResponse(prev => !prev)}
              className="size-3.5"
            />
            <span className="text-xs">Trim Response</span>
          </label>
        </div>
      </div>

      {/* Global Chat IDs */}
      <div>
        <Label className="text-xs mb-1.5">Global Chat IDs</Label>
        <p className="text-[11px] text-slate-500 mb-2">
          Fill in IDs below to run chatId tests once per chat type. Leave empty to skip that type.
        </p>
        <div className="grid grid-cols-1 gap-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="shrink-0 text-[10px] w-[78px] justify-center border-blue-300 text-blue-700">
              Contact
            </Badge>
            <Input
              value={globalContactChatId}
              onChange={e => setGlobalContactChatId(e.target.value)}
              placeholder="972548275828@c.us"
              className="h-7 text-xs"
            />
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="shrink-0 text-[10px] w-[78px] justify-center border-green-300 text-green-700">
              Group
            </Badge>
            <Input
              value={globalGroupChatId}
              onChange={e => setGlobalGroupChatId(e.target.value)}
              placeholder="120363XXX@g.us"
              className="h-7 text-xs"
            />
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="shrink-0 text-[10px] w-[78px] justify-center border-purple-300 text-purple-700">
              Newsletter
            </Badge>
            <Input
              value={globalNewsletterChatId}
              onChange={e => setGlobalNewsletterChatId(e.target.value)}
              placeholder="120363XXX@newsletter"
              className="h-7 text-xs"
            />
          </div>
        </div>
      </div>

      {/* Test list */}
      <div>
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <Label className="text-xs">
            Tests ({selectedCount}/{allTests.length})
          </Label>
          <Button variant="ghost" size="sm" onClick={handleSelectAll} className="h-5 px-1.5 text-[10px]">
            {allSelected ? 'Deselect All' : 'Select All'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSelectedTests(new Set(validTeamInboxTestIds));
            }}
            className="h-5 px-1.5 text-[10px] border-cyan-400 text-cyan-600 hover:bg-cyan-50"
          >
            Team Inbox Only
          </Button>
        </div>

        <ScrollArea className={cn('border rounded-md p-2', vertical && 'h-[300px]')}>
          {[...testsByDomain.entries()].map(([domain, tests]) => {
            const allDomainSelected = tests.every(t => selectedTests.has(t.id));
            const someDomainSelected = !allDomainSelected && tests.some(t => selectedTests.has(t.id));
            const isCollapsed = collapsedDomains.has(domain);

            return (
              <div key={domain} className="mb-3">
                {/* Domain header */}
                <div className="flex items-center gap-1.5 border-b pb-1 mb-1">
                  <button
                    onClick={() => toggleDomain(domain)}
                    className="p-0 hover:bg-slate-100 rounded"
                  >
                    {isCollapsed
                      ? <ChevronRight className="size-3.5 text-slate-400" />
                      : <ChevronDown className="size-3.5 text-slate-400" />
                    }
                  </button>
                  <span className="relative flex items-center">
                    <Checkbox
                      checked={allDomainSelected || someDomainSelected}
                      onCheckedChange={() => {
                        setSelectedTests(prev => {
                          const next = new Set(prev);
                          for (const t of tests) {
                            if (allDomainSelected) next.delete(t.id);
                            else next.add(t.id);
                          }
                          return next;
                        });
                      }}
                      className="size-3.5"
                    />
                    {someDomainSelected && (
                      <Minus className="absolute inset-0 m-auto size-2.5 text-primary pointer-events-none" />
                    )}
                  </span>
                  <span className="text-[11px] font-bold text-slate-500 capitalize">{domain}</span>
                </div>

                {/* Send Message section (custom layout) */}
                {!isCollapsed && domain === 'message' && (() => {
                  const sendTests = SEND_MESSAGE_SECTION_TEST_IDS
                    .map((id) => tests.find((t) => t.id === id))
                    .filter((t): t is DiagnosticTestDefinition => !!t);

                  if (sendTests.length === 0) return null;

                  return (
                    <div className="mb-1">
                      {sendTests.map((test) => renderUnifiedStyleToolRow(test, { stackTextFields: true }))}
                    </div>
                  );
                })()}

                {/* Schedule Message section (custom layout) */}
                {!isCollapsed && domain === 'schedule' && (() => {
                  const scheduleTests = SCHEDULE_MESSAGE_SECTION_TEST_IDS
                    .map((id) => tests.find((t) => t.id === id))
                    .filter((t): t is DiagnosticTestDefinition => !!t);

                  if (scheduleTests.length === 0) return null;

                  return (
                    <div className="mb-1">
                      {scheduleTests.map((test) => renderUnifiedStyleToolRow(test, { enableDueDateShortcuts: true, stackTextFields: true }))}
                    </div>
                  );
                })()}

                {/* Status section (custom layout with uploader) */}
                {!isCollapsed && domain === 'status' && (() => {
                  const statusTests = STATUS_SECTION_TEST_IDS
                    .map((id) => tests.find((t) => t.id === id))
                    .filter((t): t is DiagnosticTestDefinition => !!t);

                  if (statusTests.length === 0) return null;

                  return (
                    <div className="mb-1">
                      {statusTests.map((test) => renderUnifiedStyleToolRow(test))}
                    </div>
                  );
                })()}

                {/* Tests */}
                {!isCollapsed && tests
                  .filter(test =>
                    (domain !== 'message' || !SEND_MESSAGE_SECTION_TEST_SET.has(test.id)) &&
                    (domain !== 'schedule' || !SCHEDULE_MESSAGE_SECTION_TEST_SET.has(test.id)) &&
                    (domain !== 'status' || !STATUS_SECTION_TEST_SET.has(test.id))
                  )
                  .map(test => {
                  const hasChatId = CHAT_ID_TESTS.has(test.id);
                  const isGlobal = hasChatId && useGlobalChatIds.has(test.id);

                  const isTeamInbox = TEAM_INBOX_TESTS.has(test.id);

                  return (
                    <div key={test.id} className="flex items-center gap-1.5 py-0.5 flex-wrap">
                      <Checkbox
                        checked={selectedTests.has(test.id)}
                        onCheckedChange={() => handleTestToggle(test.id)}
                        className="size-3.5"
                      />
                      <span className="text-xs min-w-[180px]">
                        {test.name}
                        {test.safety !== 'safe' && (
                          <span className={cn('ml-1 text-[10px]', safetyColor(test.safety))}>
                            ({test.safety})
                          </span>
                        )}
                        {isTeamInbox && (
                          <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 h-4 border-cyan-400 text-cyan-600 bg-cyan-50">
                            Team Inbox
                          </Badge>
                        )}
                        {test.toolName ? (
                          <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 h-4 border-green-400 text-green-600 bg-green-50">
                            {test.toolName}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 h-4 border-slate-300 text-slate-400 bg-slate-50">
                            missing tool
                          </Badge>
                        )}
                      </span>

                      {/* Global chat ID checkbox */}
                      {hasChatId && (
                        <label className="flex items-center gap-1 cursor-pointer ml-1 shrink-0">
                          <Checkbox
                            checked={isGlobal}
                            onCheckedChange={() => handleGlobalToggle(test.id)}
                            className="size-3"
                            disabled={!selectedTests.has(test.id)}
                          />
                          <span className="text-[10px] text-slate-500">Global</span>
                        </label>
                      )}

                      {/* Per-test inputs (hide chatId/contactIds when global is on) */}
                      {test.inputs
                        .filter(input => {
                          if (!hasChatId || !isGlobal) return true;
                          // Hide chatId for standard global tests
                          if (input.name === 'chatId') return false;
                          // Hide contactIds for profile pic test when global is on
                          if (test.id === 'chat.getProfilePicUrl' && input.name === 'contactIds') return false;
                          return true;
                        })
                        .map(input => {
                          const currentValue = inputOverrides[test.id]?.[input.name];
                          const effectiveValue = currentValue !== undefined ? currentValue : input.defaultValue;
                          const isDisabled = !selectedTests.has(test.id);

                          if (input.name === 'fileMimeType') {
                            return null;
                          }

                          if (input.name === 'fileDataUrl') {
                            const fileInputId = `diagnostic-file-${test.id.replace(/\./g, '-')}`;
                            const normalizedFileValue = typeof currentValue === 'string' ? currentValue.trim() : '';
                            const hasSelectedFile = normalizedFileValue.length > 0;
                            const isRemoteUploadUrl = /^https?:\/\//i.test(normalizedFileValue);
                            const fileTooltip = !hasSelectedFile
                              ? 'Choose file'
                              : isRemoteUploadUrl
                                ? `Upload URL: ${normalizedFileValue}`
                                : 'Upload URL: local data URL';

                            return (
                              <div key={input.name} className="flex items-center gap-1 shrink-0">
                                <label
                                  htmlFor={fileInputId}
                                  title={fileTooltip}
                                  aria-label={fileTooltip}
                                  className={cn(
                                    'inline-flex h-6 w-6 items-center justify-center rounded-md border',
                                    hasSelectedFile ? 'border-emerald-400 bg-emerald-50' : 'bg-white',
                                    isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-slate-50'
                                  )}
                                >
                                  <Paperclip className={cn('size-3.5', hasSelectedFile ? 'text-emerald-700' : 'text-slate-600')} />
                                </label>
                                <input
                                  id={fileInputId}
                                  type="file"
                                  accept="image/*,video/*,*/*"
                                  disabled={isDisabled}
                                  className="hidden"
                                  onChange={async (e) => {
                                    const inputEl = e.currentTarget as HTMLInputElement | null;
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    try {
                                      await handleDiagnosticFileSelected(test.id, file);
                                    } finally {
                                      if (inputEl) {
                                        inputEl.value = '';
                                      }
                                    }
                                  }}
                                />
                                {hasSelectedFile && (
                                  <button
                                    type="button"
                                    className={cn(
                                      'inline-flex h-5 w-5 items-center justify-center rounded border border-red-300 bg-red-50 text-red-600',
                                      isDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-red-100'
                                    )}
                                    disabled={isDisabled}
                                    onClick={() => {
                                      handleInputChange(test.id, 'fileDataUrl', '');
                                      handleInputChange(test.id, 'fileName', '');
                                      handleInputChange(test.id, 'fileMimeType', '');
                                    }}
                                    title="Clear file"
                                    aria-label="Clear file"
                                  >
                                    <X className="size-3" />
                                  </button>
                                )}
                              </div>
                            );
                          }

                          // Boolean inputs - render as checkbox with label
                          if (input.type === 'boolean') {
                            const isChecked = effectiveValue === true || effectiveValue === 'true';
                            return (
                              <label
                                key={input.name}
                                className={cn(
                                  "flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] cursor-pointer shrink-0",
                                  isChecked ? "bg-blue-50 border-blue-300" : "bg-slate-50 border-slate-200",
                                  isDisabled && "opacity-50 cursor-not-allowed"
                                )}
                                title={(input as any).description || input.label}
                              >
                                <Checkbox
                                  checked={isChecked}
                                  onCheckedChange={(checked) => handleInputChange(test.id, input.name, checked)}
                                  disabled={isDisabled}
                                  className="size-3"
                                />
                                <span className={cn(isChecked ? "text-blue-700" : "text-slate-600")}>
                                  {input.label}
                                </span>
                                {(input as any).description && (
                                  <Info className="size-3 text-slate-400" />
                                )}
                              </label>
                            );
                          }

                          // Number inputs
                          if (input.type === 'number') {
                            return (
                              <div key={input.name} className="flex items-center gap-1 shrink-0" title={(input as any).description || input.label}>
                                <span className="text-[9px] text-slate-500">{input.label}:</span>
                                {(input as any).description && (
                                  <Info className="size-3 text-slate-400" />
                                )}
                                <Input
                                  type="number"
                                  value={currentValue ?? ''}
                                  onChange={e => handleInputChange(test.id, input.name, e.target.value)}
                                  placeholder={input.defaultValue !== undefined ? String(input.defaultValue) : ''}
                                  disabled={isDisabled}
                                  className="h-6 text-[11px] w-[60px]"
                                />
                              </div>
                            );
                          }

                          // String inputs (default)
                          const isReactionEmojiInput = test.id === 'message.reactToMessage' && input.name === 'text';
                          return (
                            <div key={input.name} className="flex items-center gap-1 shrink-0" title={(input as any).description || input.label}>
                              <span className="text-[9px] text-slate-500">{input.label}:</span>
                              {(input as any).description && (
                                <Info className="size-3 text-slate-400" />
                              )}
                              <Input
                                type="text"
                                value={currentValue ?? ''}
                                onChange={e => handleInputChange(test.id, input.name, e.target.value)}
                                placeholder={
                                  input.defaultResolver
                                    ? RESOLVER_PLACEHOLDERS[input.defaultResolver] ?? `(${input.defaultResolver})`
                                    : input.defaultValue !== undefined
                                      ? String(input.defaultValue)
                                      : ''
                                }
                                disabled={isDisabled}
                                className={cn(
                                  "h-6 text-[11px]",
                                  isWideInputField(input.name) ? "w-[280px]" : "w-[120px]"
                                )}
                              />
                              {isReactionEmojiInput && (
                                <ReusableEmojiPickerButton
                                  disabled={isDisabled}
                                  onEmojiSelect={emoji => handleInputChange(test.id, input.name, emoji)}
                                />
                              )}
                            </div>
                          );
                        })}

                      {/* Pagination buttons for tests with paginationEnabled */}
                      {(test as any).paginationEnabled && (
                        <div className="flex items-center gap-1 ml-2 shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 px-2 text-[10px]"
                            disabled={!selectedTests.has(test.id) || (inputOverrides[test.id]?.offset ?? 0) === 0}
                            onClick={() => {
                              const currentOffset = inputOverrides[test.id]?.offset ?? 0;
                              const limit = inputOverrides[test.id]?.limit ?? 20;
                              handleInputChange(test.id, 'offset', Math.max(0, currentOffset - limit));
                            }}
                            title="Previous page"
                          >
                            <ChevronLeft className="size-3" />
                            Prev
                          </Button>
                          <span className="text-[9px] text-slate-500 px-1">
                            Page {Math.floor((inputOverrides[test.id]?.offset ?? 0) / (inputOverrides[test.id]?.limit ?? 20)) + 1}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 px-2 text-[10px]"
                            disabled={!selectedTests.has(test.id)}
                            onClick={() => {
                              const currentOffset = inputOverrides[test.id]?.offset ?? 0;
                              const limit = inputOverrides[test.id]?.limit ?? 20;
                              handleInputChange(test.id, 'offset', currentOffset + limit);
                            }}
                            title="Next page"
                          >
                            Next
                            <ChevronRight className="size-3" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 px-1 text-[10px]"
                            disabled={!selectedTests.has(test.id) || (inputOverrides[test.id]?.offset ?? 0) === 0}
                            onClick={() => handleInputChange(test.id, 'offset', 0)}
                            title="First page"
                          >
                            <ChevronsLeft className="size-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </ScrollArea>
      </div>

    </div>
  );

  // ── Fixed Run button on left side ──────────────────────────────────────

  const canRun = selectedCount > 0;

  const fixedRunButton = (
    <div className="fixed right-6 bottom-6 z-[9999]">
      <Button
        onClick={handleRun}
        disabled={isLoading || !canRun}
        size="lg"
        className="min-w-[220px] shadow-2xl rounded-full text-base font-semibold tracking-wide px-8 py-3"
      >
        {isLoading ? (
          <span className="flex items-center gap-2">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Running...
          </span>
        ) : (
          `Run Diagnostics (${selectedCount})`
        )}
      </Button>
    </div>
  );

  // ── Results panel ───────────────────────────────────────────────────────

  const resultsPanel = (
    <div
      className={cn(
        'flex flex-col',
        !vertical && 'flex-1 min-w-0 border-l pl-4 sticky top-4 self-start',
        vertical && 'border-t pt-4 mt-4'
      )}
    >
      <Label className="text-xs mb-1.5">Results</Label>
      {!results && !isLoading ? (
        <p className="text-sm text-slate-500 mt-4 text-center">
          Run diagnostics to see results here.
        </p>
      ) : (
        <DiagnosticsResultsTree data={results} loading={isLoading} />
      )}
    </div>
  );

  // ── Toast ───────────────────────────────────────────────────────────────

  return (
    <>
      <div className={cn('flex gap-4', vertical ? 'flex-col' : 'flex-row min-h-[400px]')}>
        {formPanel}
        {resultsPanel}
      </div>

      {/* Fixed Run button on left side */}
      {fixedRunButton}

      {/* Simple toast */}
      {toast && (
        <div
          className={cn(
            'fixed bottom-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-md shadow-lg text-sm',
            toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          )}
        >
          {toast.message}
        </div>
      )}
    </>
  );
}
